/**
   Manager.js - Core Manager Singleton for DevCore Framework
   
   This file serves as the central manager singleton that initializes and configures
   the Vue.js application with all necessary components, models, and configurations.
   
   Key Features:
   - Initializes the Vue application with custom configuration
   - Manages component registration
   - Handles model integration
   - Provides access to core framework utilities
   
   Usage:
   import {createManager} from 'devcore'
   createManager(params, config)
   
   The vue, vuetify and router references are saved in State.ref (non-reactive).
   The app configuration is saved in State.config.
 */

// Core framework imports
import {initApp} from '@/paks/vu-app/App.js'
import UI from '../ui.json5'
import {Roles} from '@/paks/mgr-data'
import * as Models from '@/paks/mgr-models'

// Vue component imports - Core application components
import VuSign from '@/paks/vu-sign'
import {Dash, DashList, DisplayList, Page, PageList} from '@/paks/vu-dash'
import Forgot from '@/paks/vu-auth/Forgot.vue'
import Login from '@/paks/vu-auth/Login.vue'
import Logout from '@/paks/vu-auth/Logout.vue'
import VuManager from './Manager.vue'

// Feature-specific component imports
import FeatureEdit from '@/views/Designer/FeatureEdit.vue'
import AssetEdit from '@/views/Designer/AssetEdit.vue'
import AccountSettings from '@/views/Account/AccountSettings.vue'
import DeviceClaim from '@/views/Devices/DeviceClaim.vue'
import DeviceList from '@/views/Devices/DeviceList.vue'
import DeviceRelease from '@/views/Devices/DeviceRelease.vue'
import DeviceReleaseAction from '@/views/Devices/DeviceReleaseAction.vue'
import GenericTable from '@/views/Data/GenericTable.vue'
import GenericEdit from '@/views/Data/GenericEdit.vue'
import UserList from '@/views/Users/UserList.vue'
import UserPassword from '@/views/Users/UserPassword.vue'
import UserProfile from '@/views/Users/UserProfile.vue'
import Home from '@/components/Home.vue'
import {AlertEdit, AlertList} from '@/paks/vu-alerts'

/**
   ManagerClass - Core application manager class
   
   This class handles the initialization and configuration of the application,
   including component registration and model management.
 */
class ManagerClass {
    config = null
    params = null

    /**
       Creates and initializes the application manager
       @param {Object} params - Application parameters including models and components
       @param {Object} config - Application configuration including UI and roles
       @returns {ManagerClass} - Returns the initialized manager instance
     */
    create(params, config = {}) {
        // Initialize parameters with default models and custom models
        this.params = Object.assign({}, params)
        this.params.models = {
            ...Models,
            ...params.models,
        }

        // Register all application components
        this.params.components = {
            ...params.components,
            AlertEdit,
            AlertList,
            AccountSettings,
            AssetEdit,
            Dash,
            DashList,
            DeviceClaim,
            DeviceList,
            DeviceRelease,
            DeviceReleaseAction,
            DisplayList,
            FeatureEdit,
            Forgot,
            GenericTable,
            GenericEdit,
            Home,
            Login,
            Logout,
            Page,
            PageList,
            UserPassword,
            UserList,
            UserProfile,
            VuManager,
            VuSign,
        }

        // Merge UI configuration with roles and custom config
        this.config = Object.assign({}, UI, Roles, config)
        
        // Initialize the application
        initApp(this.params, this.config)
        return this
    }
}

// Create singleton instance
const Manager = new ManagerClass()

/**
   Creates and initializes the application manager
   @param {Object} params - Application parameters
   @param {Object} config - Application configuration
   @returns {ManagerClass} - Initialized manager instance
 */
export function createManager(params, config) {
    return Manager.create(params, config)
}

// Export models and framework utilities
export {Models}
export * from '@/paks/vu-app'
